import { motion } from 'motion/react';
import { Gift, CreditCard, Store, Heart, Copy } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/11.jpeg`;
const CARD_BG = 'rgba(252, 235, 208, 0.75)';
const SECTION_OVERLAY = 'rgba(252, 235, 208, 0.6)';

export function GiftRegistry() {
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const snackbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowSnackbar(true);
      if (snackbarTimeoutRef.current) clearTimeout(snackbarTimeoutRef.current);
      snackbarTimeoutRef.current = setTimeout(() => {
        setShowSnackbar(false);
        snackbarTimeoutRef.current = null;
      }, 2000);
    } catch {
      /* fallback si clipboard no disponible */
    }
  };

  useEffect(() => {
    return () => {
      if (snackbarTimeoutRef.current) clearTimeout(snackbarTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const enterStart = vh * 0.7;
      const enterEnd = -vh * 0.15;
      const progress = Math.max(0, Math.min(1, (enterStart - rect.top) / (enterStart - enterEnd)));
      setScrollProgress(progress);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const registries = [
    { name: 'Liverpool', icon: Store, description: 'Encuentra nuestra mesa de regalos', eventCode: '51969809', url: 'https://mesaderegalos.liverpool.com.mx/milistaderegalos/51969809', showEventCode: true },
    { name: 'Amazon', icon: Gift, description: 'Lista de deseos en Amazon', url: 'https://www.amazon.com.mx/wedding/share/magnoboda-samy-dany', showEventCode: false },
  ];

  const headerProgress = Math.max(0, Math.min(1, scrollProgress / 0.4));

  return (
    <TooltipProvider>
    <section
      ref={sectionRef}
      className="snap-section relative min-h-screen py-16 md:py-24 overflow-visible flex items-center justify-center"
    >
      <div className="section-content w-full px-6 flex flex-col justify-center">
        <div
          className="section-bg-image absolute inset-0 z-0"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          aria-hidden
        />
        <div className="absolute inset-0 z-[1]" style={{ backgroundColor: SECTION_OVERLAY }} />
        <div className="relative z-10 w-full max-w-4xl mx-auto">
          <div
            className="text-center mb-16 transition-all duration-700 ease-out"
            style={{
              opacity: headerProgress,
              transform: `translateY(${24 * (1 - headerProgress)}px)`,
            }}
          >
            <h2 className="text-editorial-title text-2xl md:text-3xl tracking-[0.3em] mb-4">
              MESA DE REGALOS
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-4"
              style={{ color: 'var(--emerald)', opacity: 0.85, fontFamily: 'var(--font-serif)' }}
            >
              Tu presencia es nuestro mayor regalo, pero si deseas obsequiarnos algo, aquí te dejamos nuestras opciones
            </p>
            <div className="w-20 h-px mx-auto" style={{ backgroundColor: 'var(--chartreuse)' }} />
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {registries.map((registry, index) => {
              const Icon = registry.icon;
              const stagger = index * 0.08;
              const cardProgress = Math.max(0, Math.min(1, (scrollProgress - 0.25 - stagger) / 0.35));
              const opacity = cardProgress;
              const translateY = 28 * (1 - cardProgress);
              return (
                <div
                  key={registry.name}
                  className="rounded-xl p-8 border backdrop-blur-sm min-h-0 overflow-visible transition-all duration-700 ease-out"
                  style={{
                    borderColor: 'rgba(186, 159, 56, 0.3)',
                    backgroundColor: CARD_BG,
                    opacity,
                    transform: `translateY(${translateY}px)`,
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-6 border shrink-0"
                    style={{
                      backgroundColor: 'var(--pale-lime)',
                      borderColor: 'var(--chartreuse)',
                    }}
                  >
                    <Icon className="w-8 h-8" style={{ color: 'var(--emerald)' }} />
                  </div>
                  <h3 className="text-editorial-title text-xl tracking-[0.15em] mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
                    {registry.name.toUpperCase()}
                  </h3>
                  <p className="mb-4 break-words overflow-visible" style={{ color: 'var(--emerald)', opacity: 0.85 }}>
                    {registry.description}
                  </p>
                  {registry.showEventCode && registry.eventCode && (
                    <div
                      className="rounded-lg p-4 mb-6"
                      style={{ backgroundColor: 'var(--pale-lime)', opacity: 0.8 }}
                    >
                      <p className="text-editorial-meta text-xs mb-1">Código de evento</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-serif flex-1" style={{ color: 'var(--chartreuse)' }}>{registry.eventCode}</p>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(registry.eventCode)}
                              className="shrink-0 p-2 rounded-md border transition-colors hover:bg-[var(--pale-lime)]"
                              style={{ borderColor: 'var(--chartreuse)', color: 'var(--emerald)' }}
                              aria-label="Copiar código"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Copiar</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                  <a
                    href={registry.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 rounded-full text-center border transition-colors hover:bg-[var(--pale-lime)]/50"
                    style={{
                      borderColor: 'var(--chartreuse)',
                      color: 'var(--emerald)',
                      fontFamily: 'var(--font-serif)',
                      fontSize: '0.875rem',
                    }}
                  >
                    Ver mesa de regalos
                  </a>
                </div>
              );
            })}
          </div>

          <div
            className="max-w-xl mx-auto transition-all duration-700 ease-out"
            style={{
              opacity: Math.max(0, Math.min(1, (scrollProgress - 0.55) / 0.3)),
              transform: `translateY(${28 * (1 - Math.min(1, (scrollProgress - 0.55) / 0.3))}px)`,
            }}
          >
            <div
              className="rounded-xl p-8 border overflow-visible"
              style={{
                backgroundColor: CARD_BG,
                borderColor: 'rgba(186, 159, 56, 0.3)',
              }}
            >
              <div className="text-center mb-6">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border"
                  style={{
                    backgroundColor: 'var(--emerald)',
                    borderColor: 'var(--chartreuse)',
                  }}
                >
                  <CreditCard className="w-8 h-8" style={{ color: 'var(--butter)' }} />
                </div>
                <h3 className="text-editorial-title text-lg tracking-[0.15em] mb-2">
                  TRANSFERENCIA BANCARIA
                </h3>
                <p style={{ color: 'var(--emerald)', opacity: 0.85 }}>
                  También puedes contribuir directamente a nuestra cuenta
                </p>
              </div>

              {!showBankDetails ? (
                <button
                  type="button"
                  onClick={() => setShowBankDetails(true)}
                  className="w-full py-3 rounded-full border transition-colors hover:bg-[var(--pale-lime)]/40"
                  style={{
                    borderColor: 'var(--chartreuse)',
                    color: 'var(--emerald)',
                    fontFamily: 'var(--font-serif)',
                  }}
                >
                  Ver datos bancarios
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {[
                    { label: 'Banco', value: 'BBVA Bancomer' },
                    { label: 'CLABE', value: '012 180 01574346848 8' },
                    { label: 'Titular', value: 'Daniel Navarro' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg p-4"
                      style={{ backgroundColor: 'var(--pale-lime)', opacity: 0.9 }}
                    >
                      <p className="text-editorial-meta text-xs mb-1">{item.label}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-base flex-1" style={{ color: 'var(--emerald)' }}>{item.value}</p>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(item.value)}
                              className="shrink-0 p-2 rounded-md border transition-colors hover:bg-[var(--pale-lime)]"
                              style={{ borderColor: 'var(--chartreuse)', color: 'var(--emerald)' }}
                              aria-label={`Copiar ${item.label}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Copiar</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>

          <div
            className="text-center mt-14 transition-all duration-700 ease-out"
            style={{
              opacity: Math.max(0, Math.min(1, (scrollProgress - 0.8) / 0.2)),
              transform: `translateY(${20 * (1 - Math.min(1, (scrollProgress - 0.8) / 0.2))}px)`,
            }}
          >
            <Heart className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--chartreuse)' }} />
            <p className="text-lg italic" style={{ fontFamily: 'var(--font-serif)', color: 'var(--emerald)', opacity: 0.9 }}>
              ¡Gracias por ser parte de nuestro día especial!
            </p>
          </div>
        </div>
      </div>

      {showSnackbar && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="fixed bottom-6 right-6 z-[100] px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--emerald)' }}
        >
          Copiado
        </motion.div>
      )}
    </section>
    </TooltipProvider>
  );
}
